import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

// Lazy load redirect components
const InviteRedirect = lazy(() =>
  import('@refly-packages/ai-workspace-common/components/invite-redirect').then((m) => ({
    default: m.InviteRedirect,
  })),
);

// Lazy load redirect components
const CanvasRedirect = lazy(() =>
  import('./redirects').then((m) => ({ default: m.CanvasRedirect })),
);
const WorkspaceRedirect = lazy(() =>
  import('./redirects').then((m) => ({ default: m.WorkspaceRedirect })),
);
const ProtectedRoute = lazy(() =>
  import('./redirects').then((m) => ({ default: m.ProtectedRoute })),
);

// Components imported from web-core are already lazy-loaded
import {
  Pricing,
  ShareCanvasPage,
  ShareCodePage,
  SkillResponseSharePage,
  DocumentSharePage,
  DriveFileSharePage,
  WorkflowAppPage,
  WorkflowListPage,
  AppManager,
  MarketplacePage,
  WorkflowPage,
  ToolInstallPage,
  WorkbenchPage,
  WorkspacePage,
  LoginPage,
  HomeIndex,
  RunHistoryPage,
  RunDetailPage,
  CliAuthPage,
  SocialAccountPage,
  MediaPublishPage,
  MediaLibraryPage,
  MyAssetsPage,
  MyAssetDetailPage,
  DigitalHumanPage,
  DigitalHumanGeneratePage,
  VideoEditorPage,
} from '@refly/web-core';

// Import test page
import TestWorkflowConfigPage from './test-workflow-config';

const WechatPayPage = lazy(() =>
  import('@refly-packages/ai-workspace-common/components/settings/wechat-pay-page').then((m) => ({
    default: m.default,
  })),
);

const TemplateMarket = lazy(() =>
  import('@refly-packages/ai-workspace-common/components/template-market').then((m) => ({
    default: m.default,
  })),
);

export const RoutesList: RouteObject[] = [
  {
    path: '/',
    element: <HomeIndex />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/home-index',
    element: <HomeIndex />,
  },
  {
    path: '/cli/auth',
    element: <CliAuthPage />,
  },
  {
    path: '/invite',
    element: <InviteRedirect />,
  },
  {
    path: '/pricing',
    element: <Pricing />,
  },
  {
    path: '/share/canvas/:canvasId',
    element: <ShareCanvasPage />,
  },
  {
    path: '/share/code/:shareId',
    element: <ShareCodePage />,
  },
  {
    path: '/share/answer/:shareId',
    element: <SkillResponseSharePage />,
  },
  {
    path: '/share/doc/:shareId',
    element: <DocumentSharePage />,
  },
  {
    path: '/share/file/:shareId',
    element: <DriveFileSharePage />,
  },

  // Deprecated routes - redirect to new routes
  // TODO: deprecated and navigate to /workspace
  {
    path: '/share/pages/:shareId',
    element: <WorkspaceRedirect />,
  },
  // TODO: deprecated and navigate to /workspace
  {
    path: '/preview/canvas/:shareId',
    element: <WorkspaceRedirect />,
  },
  // TODO: deprecated and navigate to /workspace
  {
    path: '/canvas/',
    element: <WorkspaceRedirect />,
  },
  // TODO: deprecated and navigate to /workflow/:workflowId
  {
    path: '/canvas/:canvasId',
    element: <CanvasRedirect />,
  },
  // TODO: deprecated and navigate to /workspace
  {
    path: '/project/:projectId',
    element: <WorkspaceRedirect />,
  },
  // TODO: deprecated and navigate to /workflow-template/:shareId
  {
    path: '/app/:shareId',
    element: <WorkflowAppPage />,
  },
  {
    path: '/workflow-list',
    element: <WorkflowListPage />,
  },
  {
    path: '/run-history',
    element: (
      <ProtectedRoute>
        <RunHistoryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/run-history/:recordId',
    element: (
      <ProtectedRoute>
        <RunDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/app-manager',
    element: <AppManager />,
  },
  {
    path: '/marketplace',
    element: <MarketplacePage />,
  },
  {
    path: '/template-market',
    element: <TemplateMarket />,
  },
  // New SEO-optimized routes
  {
    path: '/workbench',
    element: (
      <ProtectedRoute>
        <WorkbenchPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workspace',
    element: (
      <ProtectedRoute>
        <WorkspacePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workflow/:workflowId',
    element: <WorkflowPage />,
  },
  {
    path: '/workflow/:workflowId/install-tools',
    element: (
      <ProtectedRoute>
        <ToolInstallPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/workflow-template/:shareId',
    element: <WorkflowAppPage />,
  },
  // Test route for workflow config parser
  {
    path: '/test-workflow-config',
    element: <TestWorkflowConfigPage />,
  },
  {
    path: '/wechat-pay',
    element: <WechatPayPage />,
  },
  {
    path: '/social-account',
    element: (
      <ProtectedRoute>
        <SocialAccountPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/media-publish',
    element: (
      <ProtectedRoute>
        <MediaPublishPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/media-library',
    element: (
      <ProtectedRoute>
        <MediaLibraryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/media-library/category',
    element: (
      <ProtectedRoute>
        <MediaLibraryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/my-assets',
    element: (
      <ProtectedRoute>
        <MyAssetsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/my-assets/:shareId',
    element: (
      <ProtectedRoute>
        <MyAssetDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/digital-human',
    element: (
      <ProtectedRoute>
        <DigitalHumanPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/digital-human/:humanId/generate',
    element: (
      <ProtectedRoute>
        <DigitalHumanGeneratePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/video-editor',
    element: (
      <ProtectedRoute>
        <VideoEditorPage />
      </ProtectedRoute>
    ),
  },
];
